import { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "../components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "../components/ui/dropdown-menu";
import { 
  Laptop, Plus, Search, MoreHorizontal, UserPlus, Undo2,
  Monitor, Smartphone, Headphones, Package, Printer, Server
} from "lucide-react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

const ASSET_TYPES = [
  { value: "laptop", label: "Laptop", icon: Laptop },
  { value: "monitor", label: "Monitor", icon: Monitor },
  { value: "phone", label: "Teléfono", icon: Smartphone },
  { value: "headset", label: "Headset", icon: Headphones },
  { value: "printer", label: "Impresora", icon: Printer },
  { value: "server", label: "Servidor", icon: Server },
  { value: "other", label: "Otro", icon: Package },
];

const STATUS_BADGES = {
  available: "bg-emerald-100 text-emerald-700",
  assigned: "bg-blue-100 text-blue-700",
  maintenance: "bg-amber-100 text-amber-700",
  retired: "bg-slate-100 text-slate-600",
};

const STATUS_LABELS = {
  available: "Disponible",
  assigned: "Asignado",
  maintenance: "Mantenimiento",
  retired: "Retirado",
};

const Assets = () => {
  const [assets, setAssets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [assignEmployee, setAssignEmployee] = useState("");
  const [newAsset, setNewAsset] = useState({ 
    name: "", asset_type: "laptop", serial_number: "", brand: "", model: "", description: "", purchase_date: "", value: "" 
  });

  const headers = useCallback(() => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const opts = { headers: headers(), withCredentials: true };
      const [aRes, eRes] = await Promise.all([
        axios.get(`${API}/assets`, opts),
        axios.get(`${API}/employees`, opts).catch(() => ({ data: [] })),
      ]);
      setAssets(aRes.data || []);
      setEmployees(eRes.data || []);
    } catch {
      toast.error("Error al cargar activos");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!newAsset.name) { toast.error("Nombre requerido"); return; }
    try {
      const data = { ...newAsset };
      if (data.value) data.value = parseFloat(data.value);
      await axios.post(`${API}/assets`, data, { headers: headers(), withCredentials: true });
      toast.success("Activo creado");
      setShowCreateDialog(false);
      setNewAsset({ name: "", asset_type: "laptop", serial_number: "", brand: "", model: "", description: "", purchase_date: "", value: "" });
      fetchData();
    } catch { toast.error("Error al crear activo"); }
  };

  const handleAssign = async () => {
    if (!selectedAsset || !assignEmployee) { toast.error("Selecciona un empleado"); return; }
    try {
      await axios.put(`${API}/assets/${selectedAsset.asset_id}/assign?employee_id=${assignEmployee}`, {}, { headers: headers(), withCredentials: true });
      toast.success("Activo asignado");
      setShowAssignDialog(false);
      setSelectedAsset(null);
      setAssignEmployee("");
      fetchData();
    } catch { toast.error("Error al asignar activo"); }
  };

  const handleReturn = async (assetId) => {
    try {
      await axios.put(`${API}/assets/${assetId}/return`, {}, { headers: headers(), withCredentials: true });
      toast.success("Activo devuelto");
      fetchData();
    } catch { toast.error("Error al devolver activo"); }
  };

  const getEmployeeName = (empId) => {
    const emp = employees.find(e => e.employee_id === empId);
    return emp ? `${emp.first_name} ${emp.last_name}` : empId || "-";
  };

  const getAssetIcon = (type) => {
    const found = ASSET_TYPES.find(t => t.value === type);
    return found ? found.icon : Package;
  };

  const filteredAssets = assets.filter(a => {
    const matchSearch = (a.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (a.serial_number || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (a.brand || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchType = !filterType || a.asset_type === filterType;
    return matchSearch && matchType;
  });

  const stats = {
    total: assets.length,
    available: assets.filter(a => a.status === "available").length,
    assigned: assets.filter(a => a.status === "assigned").length,
    maintenance: assets.filter(a => a.status === "maintenance").length,
  };

  if (loading) {
    return (
      <Layout title="Gestión de Activos">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      title="Gestión de Activos"
      subtitle="Control de inventario, asignación y devolución de equipos"
      actions={
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button data-testid="add-asset-btn"><Plus className="w-4 h-4 mr-2" /> Nuevo Activo</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Registrar Activo</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nombre</Label>
                  <Input value={newAsset.name} onChange={(e) => setNewAsset({ ...newAsset, name: e.target.value })} data-testid="asset-name-input" />
                </div>
                <div>
                  <Label>Tipo</Label>
                  <Select value={newAsset.asset_type} onValueChange={(v) => setNewAsset({ ...newAsset, asset_type: v })}>
                    <SelectTrigger data-testid="asset-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ASSET_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Marca</Label>
                  <Input value={newAsset.brand} onChange={(e) => setNewAsset({ ...newAsset, brand: e.target.value })} />
                </div>
                <div>
                  <Label>Modelo</Label>
                  <Input value={newAsset.model} onChange={(e) => setNewAsset({ ...newAsset, model: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>No. Serie</Label>
                  <Input value={newAsset.serial_number} onChange={(e) => setNewAsset({ ...newAsset, serial_number: e.target.value })} data-testid="asset-serial-input" />
                </div>
                <div>
                  <Label>Valor ($)</Label>
                  <Input type="number" value={newAsset.value} onChange={(e) => setNewAsset({ ...newAsset, value: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Fecha de compra</Label>
                <Input type="date" value={newAsset.purchase_date} onChange={(e) => setNewAsset({ ...newAsset, purchase_date: e.target.value })} />
              </div>
              <div>
                <Label>Descripción</Label>
                <Textarea rows={2} value={newAsset.description} onChange={(e) => setNewAsset({ ...newAsset, description: e.target.value })} />
              </div>
              <Button onClick={handleCreate} className="w-full" data-testid="save-asset-btn">Guardar Activo</Button>
            </div>
          </DialogContent>
        </Dialog>
      }
    >
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="asset-stats">
        <Card className="card-hover">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
              <Package className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
              <p className="text-sm text-slate-500">Total</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <Package className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-600">{stats.available}</p>
              <p className="text-sm text-slate-500">Disponibles</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Laptop className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-600">{stats.assigned}</p>
              <p className="text-sm text-slate-500">Asignados</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Package className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-600">{stats.maintenance}</p>
              <p className="text-sm text-slate-500">Mantenimiento</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Buscar por nombre, serie o marca..." className="pl-10" value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)} data-testid="assets-search" />
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant={!filterType ? "default" : "outline"} size="sm" onClick={() => setFilterType("")}>Todos</Button>
              {ASSET_TYPES.slice(0, 5).map(t => (
                <Button key={t.value} variant={filterType === t.value ? "default" : "outline"} size="sm" onClick={() => setFilterType(t.value)}>
                  {t.label}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredAssets.length === 0 ? (
            <div className="text-center py-12 text-slate-500" data-testid="assets-empty">
              <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">No hay activos registrados</p>
              <p className="text-sm mt-1">Registra tu primer activo para empezar</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Activo</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>No. Serie</TableHead>
                  <TableHead>Asignado a</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAssets.map((asset) => {
                  const Icon = getAssetIcon(asset.asset_type);
                  return (
                    <TableRow key={asset.asset_id} data-testid={`asset-row-${asset.asset_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4 text-slate-400" />
                          <div>
                            <p className="font-medium text-slate-900">{asset.name}</p>
                            {asset.brand && <p className="text-xs text-slate-500">{asset.brand} {asset.model}</p>}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{ASSET_TYPES.find(t => t.value === asset.asset_type)?.label || asset.asset_type}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500 font-mono">{asset.serial_number || "-"}</TableCell>
                      <TableCell className="text-sm">{asset.assigned_to ? getEmployeeName(asset.assigned_to) : "-"}</TableCell>
                      <TableCell>
                        <Badge className={STATUS_BADGES[asset.status] || "bg-slate-100"}>
                          {STATUS_LABELS[asset.status] || asset.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon"><MoreHorizontal className="w-4 h-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {asset.status === "available" && (
                              <DropdownMenuItem onClick={() => { setSelectedAsset(asset); setShowAssignDialog(true); }}>
                                <UserPlus className="w-4 h-4 mr-2" /> Asignar
                              </DropdownMenuItem>
                            )}
                            {asset.status === "assigned" && (
                              <DropdownMenuItem onClick={() => handleReturn(asset.asset_id)}>
                                <Undo2 className="w-4 h-4 mr-2" /> Devolver
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Asignar Activo: {selectedAsset?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Empleado</Label>
              <Select value={assignEmployee} onValueChange={setAssignEmployee}>
                <SelectTrigger data-testid="assign-employee-select"><SelectValue placeholder="Seleccionar empleado" /></SelectTrigger>
                <SelectContent>
                  {employees.map(e => (
                    <SelectItem key={e.employee_id} value={e.employee_id}>
                      {e.first_name} {e.last_name} - {e.department}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleAssign} className="w-full" data-testid="confirm-assign-btn">Asignar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Assets;
